import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { X, Plus, Trash2, Save, Move, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * /admin/venues/:slug/edit — POI drag-and-drop editor (Slice H+).
 *
 * Admin workflow:
 *   1. Load venue + POIs
 *   2. Pick floor (dropdown)
 *   3. Click blank space on the plan → prompted new POI (name + category)
 *   4. Drag existing pin → auto-save new (x, y) via a PATCH-style approach
 *      (delete + re-create — no need for a new endpoint since POST /pois
 *      already exists and DELETE by poi_id is available).
 *   5. Tap a pin → inline row to rename / delete.
 *
 * Only admins reach this page — the underlying endpoints already enforce
 * `require_admin`. UI just gates by user + surfaces 403 gracefully.
 */
export default function AdminVenueEditor() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [venue, setVenue] = useState(null);
  const [cats, setCats]   = useState([]);
  const [pois, setPois]   = useState([]);
  const [floorId, setFloorId] = useState("");
  const [error, setError]     = useState(null);
  const [saving, setSaving]   = useState(false);
  const [dragId, setDragId]   = useState(null);
  const [addPending, setAddPending] = useState(null); // { x, y }
  const [newName, setNewName] = useState("");
  const [newCat, setNewCat]   = useState("shop");
  const canvasRef = useRef(null);

  const [analytics, setAnalytics] = useState(null);
  const [uploading, setUploading] = useState(false);
  const activeFloor = useMemo(
    () => venue?.floors?.find(f => f.id === floorId) || venue?.floors?.[0],
    [venue, floorId],
  );

  const load = useCallback(async () => {
    setError(null);
    try {
      const [vRes, cRes] = await Promise.all([
        api.get(`/venues/${slug}`),
        api.get("/venues/meta/categories"),
      ]);
      setVenue(vRes.data);
      setCats(cRes.data?.categories || []);
      if (!floorId && vRes.data?.floors?.length) {
        setFloorId(vRes.data.floors[0].id);
      }
      const pRes = await api.get(`/venues/${slug}/pois`, { params: { limit: 500 } });
      setPois(pRes.data?.items || []);
    } catch (e) {
      if (e?.response?.status === 403) setError("You need admin access to edit this venue.");
      else if (e?.response?.status === 404) setError("Venue not found.");
      else setError(e?.response?.data?.detail || "Could not load editor.");
    }
  }, [slug, floorId]);

  useEffect(() => { load(); }, [slug]);

  // Analytics — refreshed alongside the main load.
  useEffect(() => {
    if (!venue) return;
    api.get(`/venues/${slug}/analytics`)
      .then(r => setAnalytics(r.data))
      .catch(() => setAnalytics(null));
  }, [venue, slug, pois.length]);

  const uploadFloorImage = async (floor_id, file) => {
    if (!file) return;
    setUploading(true); setError(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/venues/${slug}/floors/${floor_id}/image`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || "Upload failed. Check storage config.");
    } finally { setUploading(false); }
  };

  const relativeXY = (ev) => {
    const el = canvasRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    const x = ((ev.clientX - rect.left) / rect.width) * 100;
    const y = ((ev.clientY - rect.top)  / rect.height) * 100;
    return { x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) };
  };

  const onCanvasClick = (ev) => {
    if (dragId) return;                       // ignore drops from drag mode
    if (ev.target.closest("[data-testid^='edit-poi-']")) return;  // clicked a pin
    const xy = relativeXY(ev);
    if (!xy || !activeFloor) return;
    setAddPending(xy);
    setNewName("");
    setNewCat("shop");
  };

  const confirmAdd = async () => {
    if (!addPending || !newName.trim() || !activeFloor) return;
    setSaving(true);
    try {
      const r = await api.post(`/venues/${slug}/pois`, {
        floor_id: activeFloor.id,
        name:     newName.trim(),
        category: newCat,
        x: addPending.x, y: addPending.y,
      });
      setPois(prev => [...prev, r.data]);
      setAddPending(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Add failed");
    } finally { setSaving(false); }
  };

  const deletePoi = async (poi_id) => {
    setSaving(true);
    try {
      await api.delete(`/venues/${slug}/pois/${poi_id}`);
      setPois(prev => prev.filter(p => p.poi_id !== poi_id));
    } catch (e) {
      setError(e?.response?.data?.detail || "Delete failed");
    } finally { setSaving(false); }
  };

  const onPinDrop = async (ev) => {
    if (!dragId) return;
    const xy = relativeXY(ev);
    if (!xy) { setDragId(null); return; }
    const p = pois.find(x => x.poi_id === dragId);
    if (!p) { setDragId(null); return; }
    // Optimistic update
    setPois(prev => prev.map(x => x.poi_id === dragId ? { ...x, x: xy.x, y: xy.y } : x));
    setSaving(true);
    try {
      // Delete + re-create with new coords — reuses existing endpoints.
      await api.delete(`/venues/${slug}/pois/${p.poi_id}`);
      const r = await api.post(`/venues/${slug}/pois`, {
        floor_id:    p.floor_id,
        name:        p.name,
        category:    p.category,
        x:           xy.x,
        y:           xy.y,
        description: p.description,
        listing_id:  p.listing_id,
      });
      setPois(prev => prev.map(x => x.poi_id === dragId ? r.data : x));
    } catch (e) {
      setError("Move failed — refresh to sync.");
    } finally {
      setSaving(false);
      setDragId(null);
    }
  };

  if (authLoading) return null;
  if (!user) {
    return (
      <div className="max-w-xl mx-auto p-8 text-center">
        <div className="text-stone-700">Please sign in as an admin to edit venues.</div>
        <Link to="/account" className="mt-3 inline-flex text-sm font-bold text-[#E05E36]">Sign in →</Link>
      </div>
    );
  }
  if (error && !venue) {
    return (
      <div className="max-w-xl mx-auto p-8 text-center" data-testid="editor-error">
        <MapPin size={32} className="mx-auto text-stone-300 mb-2" />
        <div className="heading font-bold text-lg text-stone-900">{error}</div>
        <Link to="/venues" className="mt-3 inline-flex text-sm font-bold text-[#E05E36]">Back to venues →</Link>
      </div>
    );
  }
  if (!venue) return <div className="p-8 text-stone-500 text-sm">Loading editor…</div>;

  const visible = pois.filter(p => p.floor_id === activeFloor?.id);

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 py-6" data-testid="admin-venue-editor">
      <div className="flex items-end justify-between gap-3 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-[#E05E36]">Admin · Venue map</div>
          <h1 className="heading font-black text-2xl md:text-3xl text-stone-900">{venue.name}</h1>
          <div className="text-xs text-stone-500 mt-1">
            {venue.floors.length} floor(s) · {pois.length} POIs · click empty space to add · drag pins to move
          </div>
        </div>
        <Link to={`/venue/${slug}`} className="text-xs font-bold rounded-full border border-stone-200 px-3 py-1.5 bg-white" data-testid="editor-preview">
          Preview →
        </Link>
      </div>

      {error && (
        <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2" data-testid="editor-inline-error">
          {error}
        </div>
      )}

      {/* Floor picker + image upload */}
      <div className="flex gap-1.5 flex-wrap items-center mb-3" data-testid="editor-floors">
        {venue.floors.map(f => (
          <button
            key={f.id}
            onClick={() => setFloorId(f.id)}
            className={`text-xs font-bold px-3 py-1.5 rounded-full border ${f.id === activeFloor?.id ? "bg-stone-900 text-white border-stone-900" : "bg-white border-stone-200"}`}
            data-testid={`editor-floor-${f.id}`}
          >
            {f.label}
          </button>
        ))}
        {activeFloor && (
          <label className="text-xs font-bold rounded-full px-3 py-1.5 bg-white border border-dashed border-stone-300 text-stone-700 cursor-pointer inline-flex items-center gap-1" data-testid="editor-upload-btn">
            <Save size={12} /> {uploading ? "Uploading…" : (activeFloor.image_url ? "Replace plan" : "Upload plan")}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={e => e.target.files?.[0] && uploadFloorImage(activeFloor.id, e.target.files[0])}
              data-testid="editor-upload-input"
            />
          </label>
        )}
      </div>

      {/* Analytics — top tapped POIs */}
      {analytics && analytics.total_taps > 0 && (
        <div className="mb-3 bg-white border border-stone-200 rounded-2xl p-3" data-testid="editor-analytics">
          <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-2">
            Top POIs by taps ({analytics.total_taps} total)
          </div>
          <div className="flex gap-2 flex-wrap">
            {analytics.top_pois.slice(0, 8).map(p => (
              <div key={p.poi_id} className="text-xs bg-stone-50 border border-stone-200 rounded-full px-3 py-1" data-testid={`analytics-${p.poi_id}`}>
                <span className="font-bold">{p.name}</span>
                <span className="text-stone-500"> · {p.tap_count} taps</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Editor canvas */}
      <div
        ref={canvasRef}
        onClick={onCanvasClick}
        onMouseUp={onPinDrop}
        onMouseLeave={() => setDragId(null)}
        className="relative rounded-2xl border border-stone-200 bg-stone-50 overflow-hidden aspect-[16/9] cursor-crosshair"
        data-testid="editor-canvas"
      >
        {activeFloor?.image_url ? (
          <img src={activeFloor.image_url} alt="" className="absolute inset-0 w-full h-full object-cover pointer-events-none" draggable="false" />
        ) : (
          <div className="absolute inset-0 grid place-items-center text-stone-400 text-sm pointer-events-none">
            Upload a floor image via the API to enable visual placement — you can still add POIs by coordinate.
          </div>
        )}
        {visible.map(p => (
          <button
            key={p.poi_id}
            onMouseDown={e => { e.stopPropagation(); setDragId(p.poi_id); }}
            className={`absolute -translate-x-1/2 -translate-y-1/2 rounded-full grid place-items-center shadow-md ${
              dragId === p.poi_id
                ? "bg-[#E05E36] text-white w-8 h-8 ring-4 ring-[#E05E36]/30"
                : "bg-white text-stone-900 w-6 h-6 border border-stone-300 hover:scale-110"
            }`}
            style={{ left: `${p.x}%`, top: `${p.y}%` }}
            title={`${p.name} · ${p.category}`}
            data-testid={`edit-poi-${p.poi_id}`}
          >
            <Move size={11} />
          </button>
        ))}
      </div>

      {/* POI list — inline rename/delete */}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="editor-poi-list">
        {visible.map(p => (
          <div key={p.poi_id} className="flex items-center gap-2 bg-white border border-stone-200 rounded-xl p-2 text-xs">
            <div className="flex-1 min-w-0">
              <div className="font-bold truncate">{p.name}</div>
              <div className="text-stone-500">{p.category} · ({p.x.toFixed(1)}%, {p.y.toFixed(1)}%)</div>
            </div>
            <button onClick={() => deletePoi(p.poi_id)} className="p-1.5 rounded-full hover:bg-red-50 text-red-600" data-testid={`edit-poi-del-${p.poi_id}`}>
              <Trash2 size={13} />
            </button>
          </div>
        ))}
        {visible.length === 0 && (
          <div className="text-stone-500 text-sm col-span-full text-center py-6">
            No POIs on this floor yet — click on the canvas to add the first one.
          </div>
        )}
      </div>

      {/* Add-POI modal */}
      {addPending && (
        <div className="fixed inset-0 bg-black/60 z-[9700] grid place-items-center p-6" data-testid="editor-add-modal">
          <div className="bg-white rounded-2xl p-5 max-w-sm w-full">
            <div className="flex items-center justify-between mb-3">
              <div className="heading font-bold text-lg">Add POI</div>
              <button onClick={() => setAddPending(null)} className="p-1 rounded-full hover:bg-stone-100">
                <X size={16} />
              </button>
            </div>
            <div className="space-y-2 text-sm">
              <label className="block">
                <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-1">Name</div>
                <input
                  autoFocus
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  placeholder="Café Bloom"
                  className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm"
                  data-testid="editor-add-name"
                />
              </label>
              <label className="block">
                <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-1">Category</div>
                <select
                  value={newCat}
                  onChange={e => setNewCat(e.target.value)}
                  className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm"
                  data-testid="editor-add-cat"
                >
                  {cats.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
              </label>
              <div className="text-[10px] text-stone-400">
                Coordinates: {addPending.x.toFixed(1)}%, {addPending.y.toFixed(1)}%
              </div>
              <button
                onClick={confirmAdd}
                disabled={saving || !newName.trim()}
                className="w-full mt-2 rounded-full bg-[#E05E36] text-white font-bold py-2 disabled:opacity-50 inline-flex items-center justify-center gap-1"
                data-testid="editor-add-save"
              >
                <Plus size={13} /> {saving ? "Saving…" : "Add POI"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
